/**
 * Selection model for the regional map: reducer + derived helpers.
 * Owns canonical state/layer/region selection, enforces invariants, and exposes
 * derived lookups for rendering. No side effects; pure data transformations.
 * Author: bb
 */

import { useCallback, useMemo, useReducer } from "react";
import {
  buildRegionSelectionKey,
  isAdministrativeDistrictAvailable,
  isDistrictAvailable,
  isPostalCodeAvailable,
  MapLayerId,
  normalizeBundeslandName,
  parseRegionSelectionKey,
} from "./mapHelpers";
import { LAYER_PRIORITY } from "./map-constants";
import type {
  RegionalSelection,
  RegionalSelectionEvent,
  UseSelectionModelResult,
} from "./RegionalFilter.types";

const createInitialSelection = (): RegionalSelection => ({
  state: null,
  layer: "federalStates",
  regions: new Set<string>(),
});

const clampLayerForState = (
  state: string | null,
  layer: MapLayerId
): MapLayerId => {
  if (!state) return "federalStates";
  if (layer === "administrativeDistricts" && !isAdministrativeDistrictAvailable(state)) {
    return "federalStates";
  }
  if (layer === "districts" && !isDistrictAvailable(state)) {
    return "federalStates";
  }
  if (layer === "postalCodes" && !isPostalCodeAvailable(state)) {
    return "federalStates";
  }
  return layer;
};

const preferredLayerForState = (state: string | null): MapLayerId => {
  if (!state) return "federalStates";
  if (isAdministrativeDistrictAvailable(state)) return "administrativeDistricts";
  if (isDistrictAvailable(state)) return "districts";
  return "federalStates";
};

const pruneRegionsForLayer = (
  regions: Set<string>,
  targetLayer: MapLayerId
): Set<string> => {
  const allowedPriority = LAYER_PRIORITY[targetLayer];
  const next = new Set<string>();
  const droppedStates = new Map<string, number>();
  const keptStates = new Map<string, number>();

  regions.forEach((entry) => {
    const parsed = parseRegionSelectionKey(entry);
    if (!parsed) return;
    const priority = LAYER_PRIORITY[parsed.layerId];
    if (priority > allowedPriority) {
      droppedStates.set(parsed.state, (droppedStates.get(parsed.state) ?? 0) + 1);
      return;
    }
    next.add(entry);
    keptStates.set(parsed.state, (keptStates.get(parsed.state) ?? 0) + 1);
  });

   // If we dropped all entries for a state, re-add the state-level selection to keep inclusion.
  droppedStates.forEach((_, state) => {
    if ((keptStates.get(state) ?? 0) === 0) {
      next.add(buildRegionSelectionKey("federalStates", state, state));
    }
  });

  return next;
};

const toggleRegionEntry = (
  regions: Set<string>,
  regionId: string,
  state: string,
  layer: MapLayerId
): Set<string> => {
  const next = new Set(regions);
  if (next.has(regionId)) {
    next.delete(regionId);
  } else {
    next.add(regionId);
  }

  const stateKey = buildRegionSelectionKey("federalStates", state, state);
  if (layer === "federalStates") {
    // Selecting a state clears any child selections for that state.
    const toRemove: string[] = [];
    next.forEach((entry) => {
      const parsed = parseRegionSelectionKey(entry);
      if (parsed && parsed.state === state && parsed.layerId !== "federalStates") {
        toRemove.push(entry);
      }
    });
    toRemove.forEach((entry) => next.delete(entry));
  } else {
    next.delete(stateKey);
    if (layer === "districts") {
      // Selecting a district clears any administrative-district picks for that state.
      const toRemove: string[] = [];
      next.forEach((entry) => {
        const parsed = parseRegionSelectionKey(entry);
        if (
          parsed &&
          parsed.state === state &&
          parsed.layerId === "administrativeDistricts"
        ) {
          toRemove.push(entry);
        }
      });
      toRemove.forEach((entry) => next.delete(entry));
    }
    if (layer === "postalCodes") {
      // Postal-code selection should clear broader picks for the same state.
      const toRemove: string[] = [];
      next.forEach((entry) => {
        const parsed = parseRegionSelectionKey(entry);
        if (!parsed || parsed.state !== state) return;
        if (parsed.layerId === "districts" || parsed.layerId === "administrativeDistricts") {
          toRemove.push(entry);
        }
      });
      toRemove.forEach((entry) => next.delete(entry));
    }
  }

  return next;
};

const selectionReducer = (
  state: RegionalSelection,
  action: RegionalSelectionEvent
): RegionalSelection => {
  switch (action.type) {
    case "resetSelection":
      return createInitialSelection();
    case "activateState": {
      const canonicalState = normalizeBundeslandName(action.state) ?? action.state;
      const nextLayer = clampLayerForState(canonicalState, state.layer);
      const prunedRegions = pruneRegionsForLayer(state.regions, nextLayer);
      return {
        state: canonicalState,
        layer: nextLayer,
        regions: prunedRegions,
      };
    }
    case "setLayer": {
      const targetLayer = clampLayerForState(state.state, action.layer);
      const regions = pruneRegionsForLayer(state.regions, targetLayer);
      return { ...state, layer: targetLayer, regions };
    }
    case "toggleRegion": {
      const canonicalState = normalizeBundeslandName(action.state) ?? action.state;
      const targetLayer = clampLayerForState(canonicalState, action.layer);
      const toggled = toggleRegionEntry(
        state.regions,
        action.regionId,
        canonicalState ?? action.state,
        targetLayer
      );
      const regions = pruneRegionsForLayer(toggled, targetLayer);
      if (regions.size === 0) {
        return createInitialSelection();
      }
      return {
        state: canonicalState,
        layer: targetLayer,
        regions,
      };
    }
    default:
      return state;
  }
};

/**
 * Builds and maintains the structured regional selection reducer plus derived lookups.
 */
export function useSelectionModel(): UseSelectionModelResult {
  const [selection, dispatch] = useReducer(selectionReducer, createInitialSelection());

  const availableDetailLayers = useMemo(() => {
    const layers: MapLayerId[] = ["federalStates"];
    if (selection.state && isAdministrativeDistrictAvailable(selection.state)) {
      layers.push("administrativeDistricts");
    }
    if (selection.state && isDistrictAvailable(selection.state)) {
      layers.push("districts");
    }
    if (selection.state && isPostalCodeAvailable(selection.state)) {
      layers.push("postalCodes");
    }
    return layers;
  }, [selection.state]);

  const selectedStateSet = useMemo(() => {
    if (selection.regions.size === 0) return null;
    const states = new Set<string>();
    selection.regions.forEach((entry) => {
      const parsed = parseRegionSelectionKey(entry);
      if (parsed?.state) states.add(parsed.state);
    });
    return states;
  }, [selection.regions]);

  const regionSelectionsByState = useMemo(() => {
    const map = new Map<string, Set<string>>();
    selection.regions.forEach((entry) => {
      const parsed = parseRegionSelectionKey(entry);
      if (!parsed) return;
      if (!map.has(parsed.state)) {
        map.set(parsed.state, new Set([entry]));
        return;
      }
      map.get(parsed.state)!.add(entry);
    });
    return map;
  }, [selection.regions]);

  const hasAllSelected = selection.regions.size === 0;

  const setDetailLayer = useCallback((layer: MapLayerId) => {
    dispatch({ type: "setLayer", layer });
  }, []);

  const resetSelection = useCallback(() => {
    dispatch({ type: "resetSelection" });
  }, []);

  const handleSelection = useCallback(
    (stateName: string, regionSelectionKey?: string, layerOverride?: MapLayerId) => {
      const canonicalState = normalizeBundeslandName(stateName) ?? stateName;
      const targetLayer = clampLayerForState(
        canonicalState,
        layerOverride ?? selection.layer
      );
      // State-level selection: toggle the state key while staying on the current layer.
      if (!regionSelectionKey) {
        const stateKey = buildRegionSelectionKey(
          "federalStates",
          canonicalState,
          canonicalState
        );
        dispatch({ type: "activateState", state: canonicalState });
        dispatch({
          type: "toggleRegion",
          state: canonicalState,
          regionId: stateKey,
          layer: "federalStates",
        });
        return;
      }

      // Region-level selection within the current layer (or override).
      if (selection.state !== canonicalState) {
        dispatch({ type: "activateState", state: canonicalState });
      }
      dispatch({
        type: "toggleRegion",
        state: canonicalState,
        regionId: regionSelectionKey,
        layer: targetLayer,
      });
    },
    [selection.layer, selection.state]
  );

  return {
    selection,
    activeState: selection.state,
    detailLayer: selection.layer,
    availableDetailLayers,
    hasAllSelected,
    selectedStateSet,
    regionSelectionsByState,
    handleSelection,
    setDetailLayer,
    resetSelection,
  };
}
